import React, {useEffect} from 'react';
import {createPaginationContainer, graphql} from 'react-relay';
import * as tracking from '../../../../../tracking';
import Util from '../../../util/util';
import {useIntl} from 'react-intl';
import {getClientName, getRolename, getTimeRegTypeAndDetail} from '../../../../../components/insights/reported-time-portfolio-report/reported_time_table_util';
import {hasFeatureFlag} from '../../../util/FeatureUtil';
import TimeRegFormatter from '../../../util/export-formatters/TimeRegFormatter';
import {useDataExport} from '../../../hooks/useDataExport';

const ReportedTimeCSVModal = ({viewer, relay, enabledColumns, closeModal}) => {
	const intl = useIntl();
	let visibleColumns = Object.keys(enabledColumns).filter(columnName => enabledColumns[columnName]);
	visibleColumns.unshift('projectId');

	const exportData = useDataExport(
		new TimeRegFormatter(intl, [], ['chip-right', 'milestone', 'selector'], true),
		null,
		data => data,
		_ => 'time_reg'
	);

	const getReportedTimeCSVData = timeRegistration => {
		let dataColumn = {};

		const currency = viewer.company.currency;
		const [projectName, taskName, projectId] = getTimeRegTypeAndDetail(timeRegistration, intl);

		visibleColumns.forEach(column => {
			let value;

			switch (column) {
				case 'billable':
					value = intl.formatMessage({id: timeRegistration.billable ? 'common.yes' : 'common.no'});
					break;
				case 'clientName':
					value = getClientName(timeRegistration);
					break;
				case 'cost':
					value = Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(currency), timeRegistration.cost, intl);
					break;
				case 'date':
					value = Util.CreateMomentDate(timeRegistration.year, timeRegistration.month, timeRegistration.day).format('D MMM YYYY');
					break;
				case 'notes':
					value = timeRegistration.notes?.replace(/\n/g, " ");
					break;
				case 'personIcon':
					value = timeRegistration.person.fullName;
					break;
				case 'price':
					value = Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(currency), timeRegistration.priceBaseCurrency, intl);
					break;
				case 'projectId':
					value = projectId ? 'P' + projectId : '';
					break;
				case 'project':
					value = projectName;
					break;
				case 'reportedTime':
					value = intl.formatNumber(Math.round((timeRegistration.minutesRegistered / 60) * 100) / 100);
					break;
				case 'roleName':
					value = getRolename(timeRegistration);
					break;
				case 'task':
					value = taskName;
					break;
				case 'invoiced':
					value = intl.formatMessage({id: timeRegistration.invoiced ? 'common.yes' : 'common.no'});
					break;
			}

			dataColumn[column] = value ?? '';
		});
		return dataColumn;
	};

	const getCSVData = () => {
		let csvData = [];
		viewer.company.timeRegistrations.edges.forEach(timeReg => {
			csvData.push(getReportedTimeCSVData(timeReg.node));
		});
		return {csvData, visibleColumns};
	};

	const handleCsvDownloadPress = () => {
		tracking.trackEvent('download reported time');

		const availableColumns = Object.keys(enabledColumns).map(column => {
			return {name: column, checked: enabledColumns[column]};
		});

		if (hasFeatureFlag('new_csv_format', viewer.availableFeatureFlags)) {
			exportData(
				availableColumns,
				viewer.company.timeRegistrations.edges.map(timeReg => timeReg.node),
				null,
				null,
				'CSV'
			);
		} else {
			const {csvData, visibleColumns} = getCSVData();
			let exportData =
				visibleColumns.map(column => {
					switch (column) {
						case 'clientName':
							return 'client';
						case 'personIcon':
							return 'person';
						case 'reportedTime':
							return 'reported';
						case 'roleName':
							return 'role';
						case 'projectId':
							return 'P Id';
						default:
							return column;
					}
				}) + '\r\n';
			exportData += Util.JSONToCSV(csvData);
			const fileName = `time_reg.csv`;
			Util.exportToCSV(exportData, fileName);
		}
	};

	useEffect(() => {
		if (viewer) {
			if (viewer.company.timeRegistrations.pageInfo.hasNextPage) {
				relay.loadMore(50000);
			} else {
				handleCsvDownloadPress();
				closeModal();
			}
		}
	}, [viewer]);

	return <div>{intl.formatMessage({id: 'common.time_regs_fetched_so_far'}, {count: viewer.company.timeRegistrations?.edges.length})}</div>;
};

const ReportedTimeCSVModalQuery = graphql`
	query ReportedTimeCSVModal_Query($searchQuery: TaskSearchQueryType, $pageSize: Int, $cursor: String) {
		viewer {
			actualPersonId
			component(name: "reported_time_CSV")
			...ReportedTimeCSVModal_viewer @arguments(searchQuery: $searchQuery, pageSize: $pageSize, cursor: $cursor)
		}
	}
`;

export {ReportedTimeCSVModalQuery};
export default createPaginationContainer(
	ReportedTimeCSVModal,
	{
		viewer: graphql`
			fragment ReportedTimeCSVModal_viewer on Viewer @argumentDefinitions(searchQuery: {type: "TaskSearchQueryType"}, pageSize: {type: "Int"}, cursor: {type: "String"}) {
				id
				availableFeatureFlags {
					key
				}
				company {
					currency
					timeRegistrations(first: $pageSize, after: $cursor, searchQuery: $searchQuery, sortValue: "", validateAccess: false) @connection(key: "Company_timeRegistrations", filters: []) {
						pageInfo {
							endCursor
							hasNextPage
						}
						edges {
							node {
								day
								month
								year
								notes
								billable
								invoiced
								person {
									id
									fullName
									role {
										name
									}
								}
								minutesRegistered
								task {
									name
									role {
										name
									}
									project {
										name
										fullAccessToProject
										companyProjectId
										client {
											name
										}
									}
								}
								project {
									name
									fullAccessToProject
									companyProjectId
									client {
										name
									}
								}
								idleTime {
									isInternalTime
									name
								}
								priceBaseCurrency
								cost
							}
						}
					}
				}
			}
		`,
	},
	{
		direction: 'forward',
		getConnectionFromProps({viewer}) {
			return viewer.company.timeRegistrations;
		},
		getFragmentVariables(prevVars, totalCount) {
			return {
				...prevVars,
				pageSize: totalCount,
			};
		},
		getVariables(_, {count, cursor}, fragmentVariables) {
			return {
				...fragmentVariables,
				pageSize: count,
				cursor,
			};
		},
		query: graphql`
			query ReportedTimeCSVModalRefetchQuery($searchQuery: TaskSearchQueryType, $pageSize: Int, $cursor: String) {
				viewer {
					component(name: "reported_time_CSV")
					...ReportedTimeCSVModal_viewer @arguments(searchQuery: $searchQuery, pageSize: $pageSize, cursor: $cursor)
				}
			}
		`,
	}
);
